import dayjs from 'dayjs';
import { useCallback, useContext, useEffect, useMemo, useState } from 'react';
import { useLocation } from 'react-router-dom';
import { Flex } from 'antd';
import { useFormik } from 'formik';

import {
  useAppDispatch,
  useAppSelector,
  useDeptProjectName,
  useNavigationToListRequests,
} from '@Hooks/index';
import { currentUserSelector } from '@Store/selectors/userInfoSelector';
import FormItems from '@Components/MainForm/components/FormItems/FormItems';
import { MainForm } from '@Components/index';
import {
  getAttendanceReasonsDict,
  getAttendanceTypesDict,
  getCompaniesDictSelector,
  getDepartmentsProjectsDict,
  getProjectNDepartmentsDict,
  getWorkLocationTypesAllowed,
} from '@Store/selectors/dictionarySelector';
import {
  transformCompaniesDictToOptions,
  transformDeptProjDictToOptions,
  transformDictToOptions,
  transformDictToOptionsNum,
} from '@Utils/transformToOptions';
import { dictionaryActions } from '@Store/slices/dictionarySlice';
import { validateForm } from '@Features/utils/validateForm';
import { useGetProjectsOrDepartmentsMutation } from '@Store/api/dictionaryApiSlice';
import { useSubmitHrmRequestMutation } from '@Store/api/basicHrmApiSlice';
import { FooterActions } from '@Enum/index';
import { attendanceRequestActions } from '@Features/EmployeeSelfService/requests/AttendanceRequest/store/attendanceRequestSlice';
import { NotificationContext } from '@Context/NotificationContext';

import { AttendanceRequestFormRequest } from '@Types/api/form';
import { getTotalDays } from '@Utils/transformDateTime';
import { usePostAttendanceFormMutation } from '../../store/api/attendanceRequestApiSlice';
import {
  attendanceRequestCreateRequisitionInfo,
  currentAttendanceLocation,
} from '../../store/selectors/attendanceRequestSelector';
import RequestDetailsSection from '../components/RequestDetailsSection';

import { AttendanceRequestFormValues } from '../types';
import { initialValuesForm } from '../initialValuesForm';
import { getAttendanceReqFormSchema } from '../validationScheme';
import { prepareReasonOptions } from '../utils';

const AttendanceRequestCreateForm: React.FC = () => {
  const {
    errorNotification,
    saveSuccessNotification,
    submitSuccessNotification,
  } = useContext(NotificationContext);

  const requisitionInfo = useAppSelector(
    attendanceRequestCreateRequisitionInfo
  );
  const currentAttendanceLocationType = useAppSelector(
    currentAttendanceLocation
  );
  const [getProjectsDepartments] = useGetProjectsOrDepartmentsMutation();
  const [postAttendanceForm] = usePostAttendanceFormMutation();
  const [submitForm] = useSubmitHrmRequestMutation();
  const currentUser = useAppSelector(currentUserSelector);
  const requestTypes = useAppSelector(getAttendanceTypesDict);
  const companies = useAppSelector(getCompaniesDictSelector);
  const workLocationTypes = useAppSelector(getWorkLocationTypesAllowed);
  const reasons = useAppSelector(getAttendanceReasonsDict);
  const projectNDepartments = useAppSelector(getProjectNDepartmentsDict);
  const departmentProjects = useAppSelector(getDepartmentsProjectsDict);
  const { findDeptProjName } = useDeptProjectName();
  const dispatch = useAppDispatch();
  const [disabledSaveForm, setDisableSaveForm] = useState(false);
  const [initialFormValues, setInitialFormValues] = useState(initialValuesForm);
  const [reqType, setReqType] = useState<number | null>(null);
  const { handleBackToListRequests } = useNavigationToListRequests();
  const { state } = useLocation();

  useEffect(
    () => () => {
      dispatch(attendanceRequestActions.setAttendanceRequest(null));
    },
    []
  );

  const handleSubmitForm = useCallback(
    (values: AttendanceRequestFormValues) => {
      setDisableSaveForm(true);

      let body = {
        empNumber: currentUser.empNumber,
        taReqToCompID: Number(values.taReqToCompID),
        leaveTypeID: values.leaveTypeID,
        deptProjID: values.deptProjID as number,
        groupID: Number(values.groupID),
        totDays: values.totDays,
        wlId: values.wlId,
        remarks: values.remarks,
        startDate: values.startDate,
        endDate: values.endDate,
        currentLocation: values.currentLocation,
      } as AttendanceRequestFormRequest;

      if (requisitionInfo?.requisitionNo) {
        body = {
          ...body,
          requestId: Number(requisitionInfo?.requisitionNo),
        };
      }

      postAttendanceForm(body)
        .unwrap()
        .then(({ requestId: reqId, createdDate, reqStatusId }) => {
          dispatch(
            attendanceRequestActions.setAttendanceRequest({
              createdDate,
              requestId: reqId,
              reqStatusId,
            })
          );

          submitForm(String(reqId))
            .unwrap()
            .then(() => {
              submitSuccessNotification(String(reqId));

              setDisableSaveForm(false);

              handleBackToListRequests();
            })
            .catch(({ data }) => {
              errorNotification(data);
              setDisableSaveForm(false);
            });
        })
        .catch(({ data }) => {
          errorNotification(data);
          setDisableSaveForm(false);
        });
    },
    [
      currentUser.empNumber,
      dispatch,
      errorNotification,
      handleBackToListRequests,
      postAttendanceForm,
      requisitionInfo,
      submitForm,
      submitSuccessNotification,
    ]
  );

  const onReqTypeChanged = useCallback(
    (value: number) => {
      setReqType(value);

      setInitialFormValues({
        ...initialValuesForm,
        leaveTypeID: value,
        currentLocation: currentUser?.currentLocation,
      });
    },
    [currentUser]
  );

  const validationSchema = getAttendanceReqFormSchema(reqType);

  const formik = useFormik({
    initialValues: initialFormValues,
    enableReinitialize: true,
    onSubmit: handleSubmitForm,
    validationSchema,
  });

  // check if redirect was from profile tab to create Attendance
  //  Pre-define values for create Attendance
  useEffect(() => {
    if (state) {
      const time = dayjs.utc(state?.date, 'DD.MM.YYYY');
      formik.setFieldValue('leaveTypeID', state?.requestType);
      formik.setFieldValue('startDate', time);
    }
  }, [state]);

  useEffect(() => {
    if (currentUser?.currentLocation) {
      formik.setFieldValue('currentLocation', currentUser?.currentLocation);
    }
  }, [currentUser?.currentLocation]);

  useEffect(() => {
    if (formik.values.startDate && formik.values.endDate) {
      const totalDays = getTotalDays(
        formik.values.endDate,
        formik.values.startDate
      );

      formik.setFieldValue('totDays', totalDays);
    } else {
      formik.setFieldValue('totDays', 0);
    }
  }, [formik.values.startDate, formik.values.endDate]);

  const handleSaveAsDraft = useCallback(async (): Promise<void> => {
    const isFormValid = await validateForm(validationSchema, formik);

    if (!isFormValid) return;

    const body = {
      empNumber: currentUser.empNumber,
      taReqToCompID: Number(formik.values.taReqToCompID),
      leaveTypeID: formik.values.leaveTypeID,
      deptProjID: formik.values.deptProjID as number,
      totDays: formik.values.totDays,
      groupID: Number(formik.values.groupID),
      remarks: formik.values.remarks,
      wlId: formik.values.wlId,
      startDate: formik.values.startDate,
      endDate: formik.values.endDate,
      currentLocation: formik.values.currentLocation,
    };

    postAttendanceForm(body)
      .unwrap()
      .then(({ createdDate, requestId: reqId, reqStatusId }) => {
        dispatch(
          attendanceRequestActions.setAttendanceRequest({
            createdDate,
            requestId: reqId,
            reqStatusId,
          })
        );

        saveSuccessNotification(String(reqId));

        setDisableSaveForm(false);

        handleBackToListRequests();
      })
      .catch(({ data }) => {
        errorNotification(data);

        setDisableSaveForm(false);
      });
  }, [currentUser, formik, handleBackToListRequests, validationSchema]);

  useEffect(() => {
    if (currentUser?.empNumber && companies?.length) {
      getProjectsDepartments({
        compId: Number(formik.values.taReqToCompID || companies[0]?.compId),
        empNo: currentUser.empNumber,
        isAdditionalRequest: true,
        workLocation: currentAttendanceLocationType,
      })
        .unwrap()
        .then((response) => {
          dispatch(dictionaryActions.setDepartmentsProjects(response));
        });
    }
  }, [
    currentUser.empNumber,
    formik.values.taReqToCompID,
    currentAttendanceLocationType,
    companies,
  ]);

  const mappedProjects = useMemo(() => {
    return projectNDepartments.map((project) => project.projDeptName);
  }, [projectNDepartments]);

  const handleFooterAction = useCallback(
    (action: FooterActions) => {
      if (action === FooterActions.SAVE_AS_DRAFT) {
        handleSaveAsDraft();
      }

      if (action === FooterActions.BACK_TO_REQUESTS) {
        handleBackToListRequests();
      }
    },
    [handleBackToListRequests, handleSaveAsDraft]
  );

  const reasonOptions = prepareReasonOptions(
    reasons,
    formik.values.leaveTypeID
  );

  return (
    <Flex gap={10} vertical style={{ height: '100%' }}>
      <MainForm
        formType="create"
        handleSubmit={formik.handleSubmit}
        userInfo={currentUser}
        requisitionInfo={requisitionInfo}
        disabledSaveForm={disabledSaveForm}
        onFooterAction={handleFooterAction}
      >
        <FormItems>
          <RequestDetailsSection
            label="Request Details"
            formik={formik}
            requestTypes={transformDictToOptions(requestTypes)}
            workLocationTypes={transformDictToOptionsNum(workLocationTypes)}
            projectNDepartments={mappedProjects}
            companies={transformCompaniesDictToOptions(companies)}
            departmentProjects={transformDeptProjDictToOptions(
              departmentProjects
            )}
            findDeptProjName={findDeptProjName}
            reasonsType={reasonOptions}
            onReqTypeChange={onReqTypeChanged}
          />
        </FormItems>
      </MainForm>
    </Flex>
  );
};

export default AttendanceRequestCreateForm;
